import { Container, InstanceLifetime } from '@cgs/wge';
import { DATA_ACCESS_SERVICE_TOKEN, DataAccessService } from '@cgs/data-access';
import {
  GAME_ACTION_HANDLER_TOKEN,
  GAME_WIN_COMPONENT_TOKEN,
  HUD_MODULE_TOKEN,
  HudModule,
  LOADING_SCREEN_TOKEN,
  WIN_LINES_CYCLING_CONDITION,
} from '@cgs/machines';
import {
  ITrifectaSDKAdapterParams,
  TRIFECTA_SDK_ADAPTER_TOKEN,
  TrifectaSDKAdapter,
  TrifectaSDKGameHook,
} from '../../sdk/trifecta-sdk.adapter';
import { TrifectaLoadingScreen } from '../../loading/trifecta-loading-screen';
import { TRIFECTA_LAUNCH_PARAMS_TOKEN, TrifectaLaunchParamsDto } from '../../models/trifecta-dto';
import {
  TRIFECTA_GAME_HOOK_TOKEN,
  TrifectaGameHook,
  TrifectaGameHookParams,
} from './game-hook/trifecta-game-hook';
import { TrifectaDemoGameHook } from './game-hook/trifecta-demo-game-hook';
import { TrifectaMainGameHook } from './game-hook/trifecta-main-game-hook';
import { TrifectaReplayGameHook } from './game-hook/trifecta-replay-game-hook';
import { TrifectaGameActionHandler } from './services/trifecta-game-action.handler';
import { TrifectaWinLinesCyclingCondition } from './services/trifecta-win-lines-cycling-condition';
import { TrifectaDemoDataAccessService } from './services/trifecta-demo-data-access.service';
import { TrifectaMainDataAccessService } from './services/trifecta-main-data-access.service';
import { TrifectaReplayDataAccessService } from './services/trifecta-replay-data-access.service';
import { HUD_CONTROLLER_TOKEN, TrifectaHudController } from './trifecta-hud.controller';
import { TrifectaGameWinComponent } from './components/trifecta-game-win.component';
import {
  TRIFECTA_GAME_OVERLAY_INIT_COMPONENT_TOKEN,
  TrifectaGameOverlayInitComponent,
} from './components/trifecta-game-overlay-init.component';
import {
  TRIFECTA_HUD_RESIZE_COMPONENT_TOKEN,
  TrifectaHudResizeComponent,
} from './components/trifecta-hud-resize.component';

export interface TrifectaHudModuleParams {
  sdkAdapter: ITrifectaSDKAdapterParams;
  gameHook: TrifectaGameHookParams;
}

export class TrifectaHudModule implements HudModule {
  // Must be called during GameProvider.register(): TRIFECTA_SDK_ADAPTER_TOKEN
  // is resolved by TrifectaLoadingScreen and DATA_ACCESS_SERVICE_TOKEN by
  // GameDataService before HudModule.initialize() runs. The container.has()
  // guards let a game pre-register its own implementation for any of these
  // tokens. Called on a subclass (e.g. SantaHudSdkModule.register), `new this`
  // instantiates that subclass.
  public static register(container: Container, params: TrifectaHudModuleParams): void {
    if (!container.has(LOADING_SCREEN_TOKEN)) {
      container.register(LOADING_SCREEN_TOKEN, (c) => new TrifectaLoadingScreen(c), {
        lifetime: InstanceLifetime.Singleton,
      });
    }

    if (!container.has(HUD_MODULE_TOKEN)) {
      container.register(HUD_MODULE_TOKEN, (c) => new this(c), {
        lifetime: InstanceLifetime.Singleton,
      });
    }

    if (!container.has(TRIFECTA_SDK_ADAPTER_TOKEN)) {
      container.register(
        TRIFECTA_SDK_ADAPTER_TOKEN,
        (c) => new TrifectaSDKAdapter(c, params.sdkAdapter),
        { lifetime: InstanceLifetime.Singleton }
      );
    }

    if (!container.has(GAME_ACTION_HANDLER_TOKEN)) {
      container.register(GAME_ACTION_HANDLER_TOKEN, (c) => new TrifectaGameActionHandler(c), {
        lifetime: InstanceLifetime.Singleton,
      });
    }

    if (!container.has(TRIFECTA_GAME_HOOK_TOKEN)) {
      container.register(
        TRIFECTA_GAME_HOOK_TOKEN,
        (c) => TrifectaHudModule.createGameHook(c, params.gameHook),
        { lifetime: InstanceLifetime.Singleton }
      );
    }

    if (!container.has(DATA_ACCESS_SERVICE_TOKEN)) {
      container.register(
        DATA_ACCESS_SERVICE_TOKEN,
        (c) => TrifectaHudModule.createDataAccessService(c),
        { lifetime: InstanceLifetime.Singleton }
      );
    }

    if (!container.has(WIN_LINES_CYCLING_CONDITION)) {
      container.register(
        WIN_LINES_CYCLING_CONDITION,
        (c) => new TrifectaWinLinesCyclingCondition(c),
        { lifetime: InstanceLifetime.Singleton }
      );
    }
  }

  private static createGameHook(
    container: Container,
    gameHookParams: TrifectaGameHookParams
  ): TrifectaGameHook {
    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    if (launchParams.funMode) {
      return new TrifectaDemoGameHook(container, gameHookParams);
    }
    if (launchParams.replayData) {
      return new TrifectaReplayGameHook(container, gameHookParams);
    }
    return new TrifectaMainGameHook(container, gameHookParams);
  }

  private static createDataAccessService(container: Container): DataAccessService {
    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    if (launchParams.funMode) {
      return new TrifectaDemoDataAccessService(container);
    }
    if (launchParams.replayData) {
      return new TrifectaReplayDataAccessService(container);
    }
    return new TrifectaMainDataAccessService(container);
  }

  constructor(protected readonly container: Container) {}

  public async initialize(): Promise<void> {
    const trifectaSDKAdapter = this.container.resolve<TrifectaSDKAdapter>(
      TRIFECTA_SDK_ADAPTER_TOKEN
    );
    const gameHook = this.container.resolve<TrifectaSDKGameHook>(TRIFECTA_GAME_HOOK_TOKEN);

    await trifectaSDKAdapter.initialize();

    trifectaSDKAdapter.setGameHook(gameHook);

    this.container.registerValue(HUD_CONTROLLER_TOKEN, new TrifectaHudController(this.container));

    this.container.register(
      GAME_WIN_COMPONENT_TOKEN,
      () => new TrifectaGameWinComponent(this.container),
      { lifetime: InstanceLifetime.Singleton }
    );

    if (!this.container.has(TRIFECTA_GAME_OVERLAY_INIT_COMPONENT_TOKEN)) {
      this.container.register(
        TRIFECTA_GAME_OVERLAY_INIT_COMPONENT_TOKEN,
        (container) => new TrifectaGameOverlayInitComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );
    }

    this.container.registerValue(
      TRIFECTA_HUD_RESIZE_COMPONENT_TOKEN,
      new TrifectaHudResizeComponent(this.container)
    );

    void this.container
      .resolve<TrifectaGameOverlayInitComponent>(TRIFECTA_GAME_OVERLAY_INIT_COMPONENT_TOKEN)
      .handleShowOverlay();
  }
}
